import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
dayjs.extend(timezone)
dayjs.extend(utc)
import { TimeZone, OptionalTimeZone } from './constants/timezones'
import {
  CertStatus,
  DesignSubmittalStatus,
  CertStatusPriority,
  DesignSubmittalStatusPriority,
} from './constants/statuses/deprecated'
import { addDaysToDate } from './utils/addDaysToDate'
import { alphabeticalSort, alphabeticalCompare } from './utils/alphabeticalSort'
import {
  HexColor,
  hexColorToRGB,
  hexColorToCMYK,
  PRIMARY_GRADIENT,
  SECONDARY_GRADIENT_LIGHT,
  SECONDARY_GRADIENT_DARK,
} from './constants/colorPallete'
import { dateFormatter } from './utils/DateFormatter'
import { DesignSubmittalDisposition } from './constants/dispositions'
import { Report, ReportItem } from './constants/reports/Report'
import { CSVEntity, CSVEntityItem } from './constants/csvEntity'
import {
  CSVImportInputRowJSON,
  CSVImportInputJSON,
  CSVImportOutputJSON,
  CSVImportResultJSON,
  CSVImportLatestJobResponse,
} from './interfaces/api/csvImport'
import { DUMMY_PROJECT_ID } from './constants/dummy'
import { pathify } from './utils/pathify'
import { PDFDesignLayerOptions } from './interfaces/db/pdfDesignLayers'
import {
  SharePointRegexValidation,
  SharePointFolderReference,
  SharePointParentFolder,
  SharePointParentFolderItem,
} from './constants/sharePoint'
import { elapsed } from './utils/elapsed'
import { listStringify } from './utils/listStringify'
import { isDeleted, notDeleted } from './utils/deleted'
import { GraphQLDeprecationLogRequestType } from './constants/graphqlDeprecationLog'
import {
  KeysOfType,
  NullableKeysOfType,
  OptionalKeysOfType,
} from './utils/types'
import { NotificationEventItem } from './constants/notificationEvents/NotificationEventItem'
import {
  FDCNotificationEvent,
  FDCNotificationEventItem,
} from './constants/notificationEvents/fdcs'
import {
  ConcreteNotificationEvent,
  ConcreteNotificationEventItem,
} from './constants/notificationEvents/concrete'
import { DSNotificationEvent } from './constants/notificationEvents/ds'
import { FDCConcurrenceLevelSideEffect } from './constants/concurrenceLevelSideEffects'
import { BetterEnum } from './interfaces'
import { fullName } from './utils/fullName'
import {
  CustomFieldClassType,
  CustomFieldClassTypeItem,
} from './constants/customFields/customFieldClassType'
import { CustomFieldDataType } from './constants/customFields/customFieldDataType'
import {
  CustomFieldEntity,
  CustomFieldEntityItem,
} from './constants/customFields/customFieldEntity'
import { CustomListItemStyle } from './constants/customFields/customListItemStyle'
import {
  InternalPayItemConnectionClassType,
  InternalPayItemConnectionClassTypeItem,
} from './constants/payItems/InternalPayItemConnectionClassType'
import { AuthUser } from './AuthUser'
import { AuthUserProperties } from './AuthUser/types'
import { CIMSPage, CIMSPageItem } from './constants/cimsPage'
import { CIMSModule, CIMSModuleItem } from './constants/CIMSModule'
import {
  FieldDesignChangeStatus,
  FieldDesignChangeStatusItem,
} from './constants/statuses/fdc'
import {
  QueuableRequestStatus,
  QueuableRequestStatusItem,
} from './constants/statuses/queuableRequest'
import { JobStatus, JobStatusItem } from './constants/statuses/job'
import { notEqual } from './utils/notEqual'
import {
  BasicPermissionItem,
  DeveloperPermissionItem,
  Permission,
  PermissionItem,
} from './constants/permission'
import { validateURL } from './utils/validateURL'
import { wait } from './utils/sleep'
import { MultiDimensionalMap } from './utils/multiDMap'
import {
  CASNotificationEvent,
  CASNotificationEventItem,
} from './constants/notificationEvents/cas'
import { required, requiredPromise } from './utils/required'
const SupportEmailAddress = 'cims_support@horrocks.com' as const
import {
  FilenameTemplate,
  FilenameTemplateItem,
} from './constants/filenameTemplateItems/FilenameTemplate'
import {
  DefaultFilenameTemplateItemType,
  ReportFilenameTemplateItemType,
} from './constants/filenameTemplateItems/types'
import { randomElement } from './utils/randomElement'
import { an } from './utils/an'
import {
  PhotoExportClassType,
  PhotoExportClassTypeItem,
} from './constants/photoExports/PhotoExportClassType'
import { FilenameTemplateItemCompatible } from './constants/filenameTemplateItems/classes/FilenameTemplateItemCompatible'
import { googleSheetURLFormat } from './constants/payItems'
import { IFIStatus, IFIStatusItem } from './constants/statuses/ifi'
import {
  IssueReportType,
  IssueReportTypeItem,
} from './constants/IssueReportType'
import { IFIReportType, IFIReportTypeItem } from './constants/IFIReportType'
import { IssueStatus, IssueStatusItem } from './constants/statuses/issue'
import { RFIStatus, RFIStatusItem } from './constants/statuses/rfi'
import { RFINotificationEvent } from './constants/notificationEvents/rfi'
import {
  IFINotificationEvent,
  IFINotificationEventItem,
} from './constants/notificationEvents/ifi'
import {
  IssueNotificationEvent,
  IssueNotificationEventItem,
} from './constants/notificationEvents/issue'
import { CASStatus, CASStatusItem } from './constants/statuses/cas'
import {
  LIMSAsphaltLotStatusItem,
  LIMSAsphaltLotStatus,
} from './constants/statuses/limsAsphaltLot'
import { EJStatus, EJStatusItem } from './constants/statuses/ej'
import { EJReportType, EJReportTypeItem } from './constants/EJReportType'
import {
  EJNotificationEvent,
  EJNotificationEventItem,
} from './constants/notificationEvents/ej'
import { StatusEnumItem } from './constants/statuses/StatusEnumItem'
import { resolveValueOrArray, ValueOrArray } from './utils/resolveValueOrArray'
import { FrequencyItem } from './constants/frequency'
import { DailyItem } from './constants/dailyItem'
import {
  LotCompactionLevel,
  LotCompactionLevelItem,
} from './constants/LotCompactionLevel'
import { FrequencyDefault } from './constants/frequency/defaults'

export {
  TimeZone,
  OptionalTimeZone,
  CASStatus,
  CASStatusItem,
  CertStatus,
  DesignSubmittalStatus,
  CertStatusPriority,
  DesignSubmittalStatusPriority,
  addDaysToDate,
  alphabeticalSort,
  alphabeticalCompare,
  HexColor,
  hexColorToRGB,
  hexColorToCMYK,
  PRIMARY_GRADIENT,
  SECONDARY_GRADIENT_LIGHT,
  SECONDARY_GRADIENT_DARK,
  dateFormatter,
  DesignSubmittalDisposition,
  Report,
  ReportItem,
  CSVEntity,
  CSVEntityItem,
  CSVImportInputRowJSON,
  CSVImportInputJSON,
  CSVImportOutputJSON,
  CSVImportResultJSON,
  CSVImportLatestJobResponse,
  DUMMY_PROJECT_ID,
  pathify,
  PDFDesignLayerOptions,
  SharePointRegexValidation,
  SharePointParentFolderItem,
  SharePointFolderReference,
  SharePointParentFolder,
  elapsed,
  listStringify,
  isDeleted,
  notDeleted,
  GraphQLDeprecationLogRequestType,
  KeysOfType,
  NullableKeysOfType,
  OptionalKeysOfType,
  NotificationEventItem,
  FDCNotificationEvent,
  FDCNotificationEventItem,
  ConcreteNotificationEvent,
  ConcreteNotificationEventItem,
  DSNotificationEvent,
  EJNotificationEvent,
  EJNotificationEventItem,
  FDCConcurrenceLevelSideEffect,
  FrequencyItem,
  FrequencyDefault,
  DailyItem,
  BetterEnum,
  fullName,
  CustomFieldClassType,
  CustomFieldClassTypeItem,
  CustomFieldDataType,
  CustomFieldEntity,
  CustomFieldEntityItem,
  CustomListItemStyle,
  InternalPayItemConnectionClassType,
  InternalPayItemConnectionClassTypeItem,
  googleSheetURLFormat,
  AuthUser,
  AuthUserProperties,
  CIMSPage,
  CIMSPageItem,
  CIMSModule,
  CIMSModuleItem,
  FieldDesignChangeStatus,
  FieldDesignChangeStatusItem,
  QueuableRequestStatus,
  QueuableRequestStatusItem,
  JobStatus,
  JobStatusItem,
  RFIStatus,
  RFIStatusItem,
  RFINotificationEvent,
  EJStatus,
  EJStatusItem,
  notEqual,
  Permission,
  validateURL,
  BasicPermissionItem,
  DeveloperPermissionItem,
  PermissionItem,
  wait,
  MultiDimensionalMap,
  CASNotificationEvent,
  CASNotificationEventItem,
  required,
  requiredPromise,
  SupportEmailAddress,
  FilenameTemplate,
  FilenameTemplateItem,
  DefaultFilenameTemplateItemType,
  ReportFilenameTemplateItemType,
  FilenameTemplateItemCompatible,
  randomElement,
  an,
  PhotoExportClassType,
  PhotoExportClassTypeItem,
  StatusEnumItem,
  IFIStatus,
  IFIStatusItem,
  IFINotificationEvent,
  IFINotificationEventItem,
  IFIReportType,
  IFIReportTypeItem,
  IssueReportType,
  IssueReportTypeItem,
  IssueStatus,
  IssueStatusItem,
  IssueNotificationEventItem,
  IssueNotificationEvent,
  LIMSAsphaltLotStatusItem,
  LIMSAsphaltLotStatus,
  EJReportType,
  EJReportTypeItem,
  resolveValueOrArray,
  ValueOrArray,
  LotCompactionLevel,
  LotCompactionLevelItem,
}
